import React, { useState, useCallback } from 'react';
import { Plus, Camera, FileText, ArrowLeft, Sparkles, Upload, X, Image as ImageIcon, AlertCircle, Brain, Zap, Package, TrendingUp, CheckCircle } from 'lucide-react';
import { useToast } from '../ui/ToastContext';
import ViewLayout from '../ui/ViewLayout';
import aiModelService from '../../utils/aiModelService';
import { uploadPropImages } from '../../utils/wasabiService';
import useCategories from '../../hooks/useCategories';

const AddPropView = ({ setActiveView, openAddProp, saveProp, currentUser, organizations }) => {
  const { toast } = useToast();
  const { categories } = useCategories();
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [currentStage, setCurrentStage] = useState('upload');
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [reviewQueue, setReviewQueue] = useState([]);

  const handleStandardAdd = () => {
    openAddProp();
  };

  const handleSmartAdd = () => {
    setSelectedMethod('smart');
  };

  // Handle file selection with base64 conversion
  const handleFileSelect = useCallback(async (files) => {
    const fileArray = Array.from(files);
    
    for (const file of fileArray) {
      try {
        // Convert to base64 immediately
        const base64Data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = reader.result; // Keep full data URL (data:image/jpeg;base64,...)
            resolve(base64);
          };

          reader.onerror = () => reject(new Error('Failed to read file'));
          reader.readAsDataURL(file);
        });
        
        const newImage = {
          id: Date.now() + Math.random(),
          file: file,
          originalName: file.name || 'unknown.jpg',
          tempName: `temp_${Date.now()}_${Math.random().toString(36).substring(2)}.${(file.name || 'unknown.jpg').split('.').pop()}`,
          previewUrl: base64Data, // Use base64 for preview
          base64Data: base64Data, // Store base64 for AI processing
          size: file.size,
          type: file.type,
          status: 'pending'
        };
        
        setUploadedImages(prev => [...prev, newImage]);
      } catch (error) {
        console.error('Error converting file to base64:', file.name, error);
      }
    }
  }, []);

  // Handle drag and drop
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    
    if (files.length > 0) {
      handleFileSelect(files);
    }
  }, [handleFileSelect]);

  // Handle file input change
  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files).filter(file => 
      file.type.startsWith('image/')
    );
    
    if (files.length > 0) {
      handleFileSelect(files);
    }
    
    // Reset input
    e.target.value = '';
  };

  // Remove image from upload queue
  const removeImage = (imageId) => {
    setUploadedImages(prev => prev.filter(img => img.id !== imageId));
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Submit approved props to database with Wasabi image upload
  const submitApprovedProps = async () => {
    const approvedProps = reviewQueue.filter(item => item.status === 'approved');
    
    if (approvedProps.length === 0) {
      toast.warning('No approved props to submit.');
      return;
    }

    try {
      console.log('=== STARTING PROP SUBMISSION WITH WASABI ===');
      console.log('Approved props to submit:', approvedProps.length);
      
      const results = [];
      
      for (let i = 0; i < approvedProps.length; i++) {
        const item = approvedProps[i];
        const aiProp = item.aiResult.propInfo;
        
        console.log(`\n--- Processing prop ${i + 1}/${approvedProps.length} ---`);
        
        // Category mapping from AI to existing categories
        const categoryMap = {
          'Tools & Hardware': 'Other',
          'Clothing & Accessories': 'Costumes',
          'Household Items': 'Decor',
          'Kitchen': 'Decor',
          'Office': 'Other',
          'Sports': 'Other',
          'Vehicles': 'Other',
          'Books': 'Other'
        };
        
        const mappedCategory = categoryMap[aiProp.category] || aiProp.category;
        const isKnownCategory = categories.includes(mappedCategory);
        
        // Convert AI data to proper prop form format
        const propData = {
          name: aiProp.name,
          category: isKnownCategory ? mappedCategory : (categories.includes('Other') ? 'Other' : categories[0] || 'Uncategorized'),
          length: '',
          width: '',
          height: '',
          weight: '',
          condition: 'Good',
          material: Array.isArray(aiProp.materials) ? aiProp.materials : [aiProp.materials],
          tags: Array.isArray(aiProp.tags) ? aiProp.tags : [aiProp.tags],
          historical_period: aiProp.historical_period,
          historical_region: aiProp.historical_region || 'Unknown - Requires Manual Review',
          verified: false,
          retention_agreement: false,
          modification_allowed: true,
          transfer_available: true,
          is_consumable: false,
          consumable_policy: 'replace',
          private_notes: '',
          barcode: '',
          purchase_date: '',
          status: 'available',
          images: [] // Will be updated with Wasabi URLs
        };
        
        console.log('=== CREATING PROP IN DATABASE ===');
        const result = await saveProp(propData, null, currentUser, organizations);
        
        if (result && Array.isArray(result) && result.length > 0) {
          const createdProp = result[0];
          const propId = createdProp.id;
          
          console.log('✅ Prop created with ID:', propId);
          
          // Find the corresponding image file
          const imageData = uploadedImages.find(img => img.id === item.id);
          
          if (imageData && imageData.file) {
            console.log('☁️ Uploading image to Wasabi...');
            
            // Upload image to Wasabi
            const uploadResults = await uploadPropImages([imageData.file], propId);
            const imageUrls = uploadResults.map(result => result.url);
            
            console.log('✅ Image uploaded to Wasabi:', imageUrls[0]);
            
            // Update prop with Wasabi image URL
            const updatedPropData = {
              ...propData,
              images: imageUrls,
              private_notes: `AI-generated prop. Confidence: ${aiProp.ai_confidence}%. Detected as: ${aiProp.detected_class}. Original AI category: "${aiProp.category}" → Mapped to: "${propData.category}". Image stored in Wasabi cloud storage.`
            };
            
            const updateResult = await saveProp(updatedPropData, createdProp, currentUser, organizations);
            console.log('📸 Prop updated with Wasabi image URL');
            
            results.push({ item, result: updateResult[0] || createdProp, success: true, imageUrl: imageUrls[0] });
          } else {
            console.log('⚠️ No image data found, saving prop without image');
            results.push({ item, result: createdProp, success: true, imageUrl: null });
          }
        } else {
          throw new Error('SaveProp function returned empty or invalid data');
        }
      }
      
      console.log('=== SUBMISSION COMPLETE ===');
      console.log('Successfully submitted:', results.length, 'props');
      
      // Update review queue to mark as submitted
      setReviewQueue(prev => prev.map(item => 
        item.status === 'approved' 
          ? { ...item, status: 'submitted', submittedAt: new Date().toISOString() }
          : item
      ));
      
      // Show success and navigate to confirmation
      setSelectedMethod('confirmation');
      
      toast.success(`Successfully added ${results.length} props with cloud images!`, {
        title: 'Props Added Successfully',
        duration: 6000
      });
      
    } catch (error) {
      console.error('=== SUBMISSION FAILED ===', error);
      toast.error(`Failed to submit props: ${error.message}`, {
        title: 'Submission Error',
        duration: 8000
      });
    }
  };

  // Start AI processing
  const startAIProcessing = async () => {
    setIsProcessing(true);
    console.log('Starting AI processing for', uploadedImages.length, 'uploaded images');
    
    try {
      const processedResults = [];
      
      // Process each uploaded image (not savedImages)
      for (let i = 0; i < uploadedImages.length; i++) {
        const image = uploadedImages[i];
        
        // Update image status to processing
        setUploadedImages(prev => prev.map(img => 
          img.id === image.id ? { ...img, status: 'processing' } : img
        ));
        
        try {
          // Check if base64 data is available
          if (!image.base64Data) {
            console.error(`❌ No base64 data available for: ${image.originalName}`);
            throw new Error('No base64 data available');
          }
          
          console.log(`📂 File details:`, {
            name: image.originalName,
            size: image.size,
            type: image.type,
            hasBase64: !!image.base64Data
          });
          
          // Create image element for AI processing
          const imgElement = new Image();
          imgElement.crossOrigin = 'anonymous';
          
          // Use base64 data instead of blob URL
          console.log(`🖼️ Using base64 data for AI processing: ${image.originalName}`);
          
          // Wait for image to load
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              console.error(`⏰ Timeout loading image: ${image.originalName}`);
              reject(new Error('Image load timeout'));
            }, 10000);
            
            imgElement.onload = () => {
              clearTimeout(timeout);
              console.log(`✅ Image loaded successfully: ${image.originalName} (${imgElement.width}x${imgElement.height})`);
              resolve();
            };
            imgElement.onerror = (err) => {
              clearTimeout(timeout);
              console.error(`❌ Image load failed: ${image.originalName}`, err);
              reject(err);
            };
            imgElement.src = image.base64Data;
          });
          
          // Run AI analysis
          console.log(`🤖 Starting AI analysis for: ${image.originalName}`);
          const aiResult = await aiModelService.analyzeImage(imgElement, image.originalName);
          console.log(`📊 AI Result for ${image.originalName}:`, aiResult);
          
          if (aiResult.success) {
            // Update image status to completed
            setUploadedImages(prev => prev.map(img => 
              img.id === image.id ? { 
                ...img, 
                status: 'completed',
                aiResult: aiResult
              } : img
            ));
            
            processedResults.push({
              imageId: image.id,
              imageName: image.originalName,
              previewUrl: image.previewUrl,
              aiResult: aiResult
            });
          } else {
            // Update image status to error
            setUploadedImages(prev => prev.map(img => 
              img.id === image.id ? { 
                ...img, 
                status: 'error',
                errorMessage: aiResult.message
              } : img
            ));
          }
          
        } catch (error) {
          console.error('Error processing image:', image.originalName, error);
          setUploadedImages(prev => prev.map(img => 
            img.id === image.id ? { 
              ...img, 
              status: 'error',
              errorMessage: 'Failed to process image'
            } : img
          ));
        }
        
        // Small delay between processing
        if (i < uploadedImages.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      console.log('AI processing completed for', processedResults.length, 'images');
      
      if (processedResults.length > 0) {
        // Add successful results to review queue
        const reviewItems = processedResults.map(result => ({
          id: result.imageId,
          imageName: result.imageName,
          previewUrl: result.previewUrl,
          aiResult: result.aiResult,
          status: 'pending_review',
          processedAt: new Date().toISOString()
        }));
        
        setReviewQueue(prev => [...prev, ...reviewItems]);
        setCurrentStage('review');
        setSelectedMethod('review');
        
        console.log('Added', reviewItems.length, 'items to review queue');
      } else {
        toast.warning('AI processing completed, but no objects were successfully identified.');
      }
      
    } catch (error) {
      console.error('Error in AI processing:', error);
      toast.error('Error during AI processing: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // If Smart Add is selected, show the Smart Add interface
  if (selectedMethod === 'smart') {
    const smartAddStats = [
      <ViewLayout.Stat 
        title="Images Uploaded" 
        value={uploadedImages.length} 
        icon={ImageIcon} 
        color="blue" 
      />,
      <ViewLayout.Stat 
        title="Processed" 
        value={uploadedImages.filter(img => img.status === 'completed').length} 
        icon={Brain} 
        color="green" 
      />,
      <ViewLayout.Stat 
        title="Processing" 
        value={uploadedImages.filter(img => img.status === 'processing').length} 
        icon={TrendingUp} 
        color="orange" 
      />
    ];

    const smartAddActions = [
      // Process with AI button
      currentStage === 'upload' && (
        <button
          onClick={startAIProcessing}
          disabled={isProcessing || uploadedImages.length === 0}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
            isProcessing || uploadedImages.length === 0
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-green-600 text-white hover:bg-green-700'
          }`}
        >
          <Sparkles className="h-4 w-4" />
          {isProcessing ? 'Processing...' : 'Start AI Recognition'}
        </button>
      )
    ].filter(Boolean);

    return (
      <ViewLayout
        title="Smart Add with AI Recognition"
        subtitle="Upload photos and let AI identify props automatically"
        breadcrumbs={[
          { label: 'Add Options', onClick: () => {
            setSelectedMethod(null);
            setUploadedImages([]);
            setIsProcessing(false);
          }}
        ]}
        stats={smartAddStats}
        actions={smartAddActions}
      >
          {/* Images Grid Display */}
          {uploadedImages.length > 0 && (
            <div className="mb-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {uploadedImages.map((image) => (
                  <div key={image.id} className="relative group bg-white rounded-lg overflow-hidden shadow-sm">
                    <div className="aspect-square">
                      <img
                        src={image.previewUrl}
                        alt={image.originalName}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-3">
                      <div className="text-xs font-medium text-gray-900 truncate">
                        {image.originalName}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {formatFileSize(image.size)}
                      </div>
                    </div>
                    <button
                      onClick={() => removeImage(image.id)}
                      className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Processing + Status Display */}
          {uploadedImages.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-green-900 mb-2">
                  🤖 AI Recognition Ready
                </h3>
                <p className="text-green-700 mb-4">
                  Run AI analysis on {uploadedImages.length} uploaded images
                </p>
                <button
                  onClick={startAIProcessing}
                  disabled={isProcessing}
                  className={`px-8 py-4 rounded-lg text-lg font-medium ${
                    isProcessing
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  <Sparkles className="h-5 w-5 inline mr-2" />
                  {isProcessing ? 'Processing...' : 'Start AI Recognition'}
                </button>
              </div>
              
              {/* Processing Status */}
              {isProcessing && (
                <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-blue-900 flex items-center gap-2">
                        <Brain className="h-4 w-4" />
                        AI Model Processing Images...
                      </div>
                      <div className="text-xs text-blue-700 mt-1">
                        Analyzing {uploadedImages.length} images with COCO-SSD object detection
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-blue-700 mb-1">
                      <span>Progress</span>
                      <span>
                        {uploadedImages.filter(img => img.status === 'completed' || img.status === 'error').length} / {uploadedImages.length}
                      </span>
                    </div>
                    <div className="bg-blue-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-500" 
                        style={{
                          width: `${(uploadedImages.filter(img => img.status === 'completed' || img.status === 'error').length / uploadedImages.length) * 100}%`
                        }}
                      ></div>
                    </div>
                  </div>
                  
                  {/* Real-time stats */}
                  <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                    <div className="bg-green-100 text-green-800 px-2 py-1 rounded text-center">
                      ✅ {uploadedImages.filter(img => img.status === 'completed').length} Identified
                    </div>
                    <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-center">
                      🔄 {uploadedImages.filter(img => img.status === 'processing').length} Processing
                    </div>
                    <div className="bg-red-100 text-red-800 px-2 py-1 rounded text-center">
                      ❌ {uploadedImages.filter(img => img.status === 'error').length} Failed
                    </div>
                  </div>
                </div>
              )}
              
              {/* Images Grid with Status Indicators */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {uploadedImages.map((image) => (
                  <div key={image.id} className="relative group bg-white rounded-lg overflow-hidden shadow-sm">
                    <div className="aspect-square">
                      <img
                        src={image.previewUrl}
                        alt={image.originalName}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-3">
                      <div className="text-xs font-medium text-gray-900 truncate">
                        {image.originalName}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {formatFileSize(image.size)}
                      </div>
                    </div>
                    
                    {/* Status Indicator */}
                    <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium ${
                      image.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      image.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                      image.status === 'completed' ? 'bg-green-100 text-green-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {image.status === 'processing' && (
                        <div className="flex items-center gap-1">
                          <div className="animate-spin rounded-full h-2 w-2 border-b border-current"></div>
                          Processing
                        </div>
                      )}
                      {image.status === 'completed' && (
                        <div className="flex items-center gap-1">
                          <Brain className="h-2 w-2" />
                          {image.aiResult?.confidence}%
                        </div>
                      )}
                      {image.status === 'pending' && 'Ready'}
                      {image.status === 'error' && 'Error'}
                    </div>
                    
                    {/* AI Results Preview */}
                    {image.status === 'completed' && image.aiResult && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                        <div className="text-white text-xs">
                          <div className="font-medium truncate">{image.aiResult.propInfo.name}</div>
                          <div className="text-gray-200 text-xs">{image.aiResult.propInfo.category}</div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload Prop Images
            </h3>
            
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragOver 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="space-y-4">
                <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <Camera className="h-8 w-8 text-gray-400" />
                </div>
                <div>
                  <p className="text-lg font-medium text-gray-900">Drop images here or click to browse</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Supports JPG, PNG, GIF • Multiple files allowed • Drag & drop enabled
                  </p>
                </div>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileInputChange}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 cursor-pointer"
                >
                  <Upload className="h-5 w-5" />
                  Choose Images
                </label>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div className="text-sm">
                <div className="font-medium text-amber-900 mb-1">📷 Photo Tips for Best AI Recognition:</div>
                <div className="text-amber-800 space-y-1">
                  <p>• Take clear, well-lit photos with the prop as the main subject</p>
                  <p>• Avoid cluttered backgrounds when possible</p>
                  <p>• Include the full prop in the frame</p>
                  <p>• Multiple angles can help with identification accuracy</p>
                </div>
              </div>
            </div>
          </div>
      </ViewLayout>
    );
  }

  // If Review is selected, show the Review Queue interface
  if (selectedMethod === 'review') {
    const reviewStats = [
      <ViewLayout.Stat 
        title="Total Props" 
        value={reviewQueue.length} 
        icon={Brain} 
        color="blue" 
      />,
      <ViewLayout.Stat 
        title="Ready to Submit" 
        value={reviewQueue.filter(item => item.status === 'approved').length} 
        icon={CheckCircle} 
        color="green" 
      />,
      <ViewLayout.Stat 
        title="Needs Review" 
        value={reviewQueue.filter(item => item.status === 'pending_review').length} 
        icon={AlertCircle} 
        color="orange" 
      />
    ];

    const reviewActions = [
      <button
        onClick={submitApprovedProps}
        disabled={reviewQueue.filter(item => item.status === 'approved').length === 0}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
          reviewQueue.filter(item => item.status === 'approved').length === 0
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-green-600 text-white hover:bg-green-700'
        }`}
      >
        <Plus className="h-4 w-4" />
        Submit {reviewQueue.filter(item => item.status === 'approved').length} Props
      </button>
    ];

    return (
      <ViewLayout
        title="Review AI-Identified Props"
        subtitle={`${reviewQueue.length} props identified and ready for review`}
        breadcrumbs={[
          { label: 'Upload Images', onClick: () => setSelectedMethod('smart') }
        ]}
        stats={reviewStats}
        actions={reviewActions}
      >

          {/* Props Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {reviewQueue.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                {/* Image */}
                <div className="aspect-square relative">
                  <img
                    src={item.previewUrl}
                    alt={item.imageName}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-1 right-1">
                    <div className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${
                      item.status === 'pending_review' ? 'bg-amber-100 text-amber-800' :
                      item.status === 'approved' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {item.aiResult.confidence}%
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-3">
                  <div className="mb-2">
                    <h3 className="font-semibold text-sm truncate">{item.aiResult.propInfo.name}</h3>
                    <p className="text-xs text-gray-600 truncate">{item.aiResult.propInfo.category}</p>
                  </div>

                  {/* AI Details - Compact */}
                  <div className="space-y-1 mb-3">
                    <div className="text-xs">
                      <span className="font-medium text-gray-700">Mat:</span>
                      <span className="ml-1 text-gray-600 truncate">
                        {item.aiResult.propInfo.materials.slice(0, 2).join(', ')}
                      </span>
                    </div>
                    <div className="text-xs">
                      <span className="font-medium text-gray-700">Tags:</span>
                      <span className="ml-1 text-gray-600 truncate">
                        {item.aiResult.propInfo.tags.slice(0, 2).join(', ')}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons - Smaller */}
                  <div className="flex gap-1">
                    <button className="flex-1 bg-blue-600 text-white py-1.5 px-2 rounded text-xs hover:bg-blue-700">
                      Edit
                    </button>
                    <button 
                      onClick={() => {
                        // Mark as approved
                        setReviewQueue(prev => prev.map(reviewItem => 
                          reviewItem.id === item.id 
                            ? { ...reviewItem, status: 'approved' }
                            : reviewItem
                        ));
                      }}
                      className={`flex-1 py-1.5 px-2 rounded text-xs ${
                        item.status === 'approved'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      {item.status === 'approved' ? '✓' : 'OK'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ViewLayout>
      );
    }

  // If confirmation is selected, show success page
  if (selectedMethod === 'confirmation') {
    const submittedProps = reviewQueue.filter(item => item.status === 'submitted');
    
    return (
      <ViewLayout
        title="Props Successfully Added!"
        subtitle={`${submittedProps.length} props have been added to your inventory with cloud storage`}
        stats={[
          <ViewLayout.Stat 
            title="Props Added" 
            value={submittedProps.length} 
            icon={CheckCircle} 
            color="green" 
          />,
          <ViewLayout.Stat 
            title="Images Stored" 
            value={submittedProps.length} 
            icon={ImageIcon} 
            color="blue" 
          />
        ]}
        actions={[
          <button
            onClick={() => setActiveView('inventory')}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <CheckCircle className="h-4 w-4" />
            View in Inventory
          </button>
        ]}
      >
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-green-900 mb-3">
            ✅ All Props Successfully Added!
          </h3>
          <p className="text-green-800 mb-4">
            Your props have been saved to the database with images automatically uploaded to Wasabi cloud storage.
          </p>
          <div className="text-sm text-green-700">
            ☁️ Images are now accessible via cloud URLs - no manual file management needed!
          </div>
        </div>
        
        {/* Show added props summary */}
        <div className="mt-6">
          <h4 className="font-medium mb-4">Added Props:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {submittedProps.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow-md p-4">
                <div className="aspect-square mb-3">
                  <img
                    src={item.previewUrl}
                    alt={item.imageName}
                    className="w-full h-full object-cover rounded"
                  />
                </div>
                <h5 className="font-medium text-sm">{item.aiResult.propInfo.name}</h5>
                <p className="text-xs text-gray-600">{item.aiResult.propInfo.category}</p>
                <div className="mt-2 flex items-center gap-1 text-xs text-green-600">
                  ☁️ Cloud Stored
                </div>
              </div>
            ))}
          </div>
        </div>
      </ViewLayout>
    );
  }



  // Default view - show both options
  const mainActions = [
    <button
      onClick={() => setActiveView('inventory')}
      className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 flex items-center gap-1"
    >
      <ArrowLeft className="h-4 w-4" />
      Back to Inventory
    </button>
  ];

  return (
    <ViewLayout
      title="Add New Prop"
      subtitle="Choose your preferred method for adding props to inventory"
      actions={mainActions}
    >

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Standard Add Option */}
        <div className="bg-white rounded-lg shadow-md p-6 border-2 border-gray-200 hover:border-blue-300 transition-colors">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold mb-3">Standard Add</h3>
            <p className="text-gray-600 mb-6">
              Manually enter all prop details using the traditional form. Best for when you have specific information ready.
            </p>
            <button
              onClick={handleStandardAdd}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto"
            >
              <Plus className="h-5 w-5" />
              Add Prop Manually
            </button>
          </div>
        </div>

        {/* Smart Add Option */}
        <div className="bg-white rounded-lg shadow-md p-6 border-2 border-green-200 hover:border-green-300 transition-colors">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <div className="relative">
                <Camera className="h-8 w-8 text-green-600" />
                <Sparkles className="h-4 w-4 text-yellow-500 absolute -top-1 -right-1" />
              </div>
            </div>
            <h3 className="text-xl font-semibold mb-3 flex items-center justify-center gap-2">
              Smart Add with AI
              <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">NEW</span>
            </h3>
            <p className="text-gray-600 mb-6">
              Upload photos and let AI automatically identify prop names, categories, materials, and historical details.
            </p>
            <button
              onClick={handleSmartAdd}
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 flex items-center gap-2 mx-auto"
            >
              <Camera className="h-5 w-5" />
              Add Props with AI
            </button>
          </div>
        </div>
      </div>

      {/* Feature Comparison */}
      <div className="mt-8 bg-gray-50 rounded-lg p-6">
        <h4 className="font-semibold mb-4 text-center">Feature Comparison</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h5 className="font-medium text-blue-600 mb-2">Standard Add</h5>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Complete control over all fields</li>
              <li>• Best for props with complex details</li>
              <li>• Familiar manual entry process</li>
              <li>• No image upload required</li>
            </ul>
          </div>
          <div>
            <h5 className="font-medium text-green-600 mb-2">Smart Add with AI</h5>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Automatic prop identification</li>
              <li>• Bulk processing of multiple photos</li>
              <li>• AI-generated tags and categories</li>
              <li>• Review and edit before saving</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Quick Tips */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h5 className="font-medium text-blue-900 mb-2">💡 Quick Tips</h5>
        <div className="text-sm text-blue-800 space-y-1">
          <p>• Use <strong>Standard Add</strong> for props that need precise, custom descriptions</p>
          <p>• Use <strong>Smart Add</strong> for quickly cataloging large inventories with photos</p>
          <p>• Smart Add works best with clear, well-lit photos of individual props</p>
        </div>
      </div>
    </ViewLayout>
  );
};

export default AddPropView;